import React, { useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { useQuery } from 'jsonapi-react'
import { useParams, Redirect, Route } from 'react-router-dom'
import { Layout } from 'app/theme/layout'
import Breadcrumbs from 'app/theme/Breadcrumbs'
import Slider from 'app/theme/slider'
import InquiryModal from './InquiryModal'
import NotFoundPage from 'app/pages/NotFoundPage'
import { TrackableButton, TrackableLink } from 'app/theme'
import { getResizedPhotoUrl } from 'app/utils/photos'
import styles from './ListingPage.module.css'
import locations from './locations'

export default function ListingPage(props) {
  const params = useParams()
  const [currentSlide, updateCurrentSlide] = useState(0)
  const [inquiryModalIsOpen, setInquiryModalIsOpen] = useState(false)

  const listingId = params.slug.split('-')[0]

  if (isNaN(listingId)) {
    return <NotFoundPage />
  }

  const { isLoading, data } = useQuery(['market_listings', listingId, {
    include: [
      'photos',
      'vehicle_features'
    ],
    fields: {
      photos: [
        'uuid',
        'path',
        'extension'
      ],
      vehicle_features: [
        'name',
        'category_id',
      ]
    }
  }])

  const featureCategories = useQuery(['vehicle_feature_categories', {
    include: ['vehicle_features'],
    fields: {
      vehicle_feature_categories: [
        'name',
        'code',
      ],
      vehicle_features: [
        'name',
        'category_id',
      ]
    }
  }])

  if (isLoading) {
    return <Layout />
  }

  if (!data) {
    return <NotFoundPage />
  }

  const location = locations.find(l => l.shortCode === data.state?.toUpperCase())

  const breadcrumbs = [
    { url: "/", text: "Home" },
    { url: "/marketplace/", text: "Marketplace" },
    { url: "/marketplace/buy-a-food-truck/", text: "Buy a Food Truck" },
  ]

  if (data.state) {
    breadcrumbs.push(
      { text: `${location?.name || data.state} Food Trucks, Carts, & Trailers for Sale`, url: `/marketplace/buy-a-food-truck/${data.state.toLowerCase()}/` },
    )
  } else {
    breadcrumbs.push(
      { text: `Trucks, Carts & Trailers for Sale`, url: `/marketplace/buy-a-food-truck/` }
    )
  }

  breadcrumbs.push({ text: data.title })

  const googleMetaTags = JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: data.title,
    image: data.photos[0]?.url,
    description: data.description,
    brand: {
      '@type': 'Brand',
      name: data.make,
    },
    offers: {
      '@type': 'Offer',
      url: `https://mobilefoodalliance.com/marketplace/buy-a-food-truck/${data.slug}/`,
      priceCurrency: 'USD',
      price: data.price,
      availability: 'https://schema.org/InStock',
      itemCondition: 'https://schema.org/UsedCondition',
    },
  })

  return (
    <Route
      render={({ staticContext }) => {
        if (staticContext && ['removed', 'sold'].includes(data.status)) {
          staticContext.status = 410
        }
        return (
          <Layout>
            <Helmet>
              <title>{`${data.title} - ${data.make} / ${data.model} / ${data.year} - For Sale - Mobile Food Alliance`}</title>
              <meta name="description" content={`Check out ${data.title}, a ${data.year} ${data.make} ${data.model} for sale on the Mobile Food Alliance Marketplace.`} />
              <meta name="og:title" content={`${data.title} - ${data.make} / ${data.model} / ${data.year} - For Sale - Mobile Food Alliance`} />
              { (!data.active || !["new", "available", "sold"].includes(data.status)) && <meta name="robots" content="noindex" /> }
              <script type="application/ld+json">
                {googleMetaTags}
              </script>
            </Helmet>

            <div className="container mt-8">
              {data.status === 'sold' ? (
                <div className="border-2 border-red-400 bg-red-200 text-red-600 p-3 mx-2 mb-6 text-center">
                  This truck has been sold.
                </div>
              ) : data.status === 'removed' ? (
                <div className="border-2 border-red-400 bg-red-200 text-red-600 p-3 mx-2 mb-6 text-center">
                  This listing has been removed.
                </div>
              ) : data.status === 'escrow' ? (
                <div className="border-2 border-orange-400 bg-orange-200 text-orange-600 p-3 mx-2 mb-6 text-center">
                  This truck is in escrow.
                </div>
              ) : null}
              <section className="lg:w-3/5 inline-block align-top p-3">
                <h1 className="font-roboto-flex text-3xl md:text-4xl text-left leading-tight">{data.title}</h1>
                <h3 className="font-roboto-flex font-light text-2xl leading-tight">{data.make}/{data.model}/{data.year}</h3>
                <div className="breadcrumbs mt-4">
                  <Breadcrumbs crumbs={breadcrumbs} className="mt-4" />
                </div>
                <TrackableButton
                  onClick={() => {
                    setInquiryModalIsOpen(true)
                  }}
                  className="font-roboto px-6 py-2 btn btn-aegean mt-2"
                >
                  Connect With Seller
                </TrackableButton>
                <p className="font-light mt-2">Wondering how Mobile Food Alliance's Marketplace works? <a className="font-roboto text-teal" href="/marketplace/faq/">Read our FAQS.</a></p>
              </section>
              <section className="lg:w-2/5 inline-block p-3">
                <div className="bg-white border p-6">
                  <a href="/marketplace/build-a-food-truck/">
                    <h3 className="font-roboto-flex text-xl lg:text-2xl text-aegean text-left leading-tight mb-3">Build a Custom Food truck</h3>
                  </a>
                  <p className="font-light text-md">Find out why building a custom food truck can be the best way to start your mobile food business.</p>
                  <TrackableLink to="/marketplace/build-a-food-truck/" className="font-roboto btn btn-white py-2 px-4 text-xl mt-4">Learn More</TrackableLink>
                </div>
              </section>
            </div>

            <div className="container mt-2 mb-6">
              <section className="w-full lg:w-3/5 inline-block align-top p-3">
                <Slider
                  className={styles.slider}
                  current={currentSlide}
                  slidesToShow={1}
                  slidesToScroll={1}
                  arrows={true}
                  dots={true}
                  lazyLoad={true}
                >
                  {data.photos.map(photo => (
                    <div className={styles.slide} key={photo.id}>
                      <img className={styles.slideImage} src={getResizedPhotoUrl(photo, null, 400)} />
                    </div>
                  ))}
                </Slider>
              </section>
              <section className="w-full lg:w-2/5 inline-block align-top p-3">
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                  {data.photos.map((photo, index) => (
                    <div key={photo.id} className="cursor-pointer" onClick={() => updateCurrentSlide(index)}>
                      <div className={[styles.grid_item]} style={{ backgroundImage: `url(${getResizedPhotoUrl(photo, 200, 200)})` }}></div>
                    </div>
                  ))}
                </div>
              </section>
            </div>

            <div className="container">
              <section className="w-full bg-white border rounded">
                <div className="bg-gray-100 text-aegean text-lg py-2 px-4 border-b rounded-t">
                  <p>ABOUT THIS TRUCK</p>
                </div>
                <div className="py-4 px-4 w-full lg:w-3/5 inline-block align-top">
                  <ul className="pl-6 list-disc">
                    <li className="font-light text-md"><strong>Title:</strong> {data.title}</li>
                    <li className="font-light text-md"><strong>Price:</strong> {data.price}</li>
                    <li className="font-light text-md"><strong>Current Location of Truck:</strong> {data.vehicle_city ? `${data.vehicle_city}, ${data.vehicle_state}` : data.location}</li>
                    <li className="font-light text-md"><strong>Year:</strong> {data.year}</li>
                    <li className="font-light text-md"><strong>Make:</strong> {data.make}</li>
                    <li className="font-light text-md"><strong>Current Permits:</strong> {data.permits}</li>
                  </ul>
                </div>
                <div className="py-4 px-4 w-full lg:w-2/5 inline-block align-top">
                  <ul className="pl-6 list-disc">
                    <li className="font-light text-md"><strong>Model:</strong> {data.model}</li>
                    <li className="font-light text-md"><strong>Mileage:</strong> {data.mileage}</li>
                    <li className="font-light text-md"><strong>Gas or Diesel:</strong> {data.fuel_type} </li>
                    <li className="font-light text-md"><strong>Length:</strong> {data.length}</li>
                    <li className="font-light text-md"><strong>Height:</strong> {data.height}</li>
                  </ul>
                </div>
              </section>

              <div className="w-full mt-4">
                <section className="py-4 lg:pr-4 w-full lg:w-3/5 inline-block align-top">
                  <div className="w-full bg-white border rounded">
                    <div className="bg-gray-100 text-aegean text-lg py-2 px-4 border-b rounded-t">
                      <p>EQUIPMENT AND FEATURES</p>
                    </div>
                    <div className="px-6 pb-6">
                      <ul className="font-light text-md pl-6 list-disc">
                        { featureCategories.data?.map(category => {
                          let children = category.vehicle_features?.reduce((acc, feature) => {
                            if (data.vehicle_features.find(f => f.id === feature.id)) {
                              acc && acc.push(feature)
                            }
                            return acc
                          }, [])

                          return children[0] ? (
                            <div key={category.id}>
                              <h3 className="font-roboto-flex font-light text-xl mt-4">{category.name}</h3>
                              <ul className="font-light text-md pl-6 list-disc">
                                {children?.map(feature => {
                                  if (data.vehicle_features.find(f => f.id === feature.id)) {
                                    return <li key={feature.id}>{feature.name}</li>
                                  } else {
                                    return null
                                  }
                                }
                                )}
                              </ul>
                            </div>
                          ) : null
                        })}
                      </ul>
                    </div>
                  </div>
                </section>
                <section className="py-4 lg:pl-4 w-full lg:w-2/5 inline-block align-top">
                  <div className="w-full bg-white border rounded">
                    <div className="bg-gray-100 text-aegean text-lg py-2 px-4 border-b rounded-t">
                      <p>ADDITIONAL DETAILS</p>
                    </div>
                    <p className="font-light text-md p-4 break-words">
                      {data.additional_equipment}
                    </p>
                  </div>

                  <div className="w-full bg-white border rounded mt-8">
                    <div className="bg-gray-100 text-aegean text-lg py-2 px-4 border-b rounded-t">
                      <p>RETURN POLICY</p>
                    </div>
                    <p className="font-light text-md p-4 break-words">
                      Sold as is, seller does not accept returns
                    </p>
                  </div>

                  <TrackableButton onClick={() => setInquiryModalIsOpen(true)} className="font-roboto btn btn-aegean py-2 px-4 w-full text-xl mt-4" title={`Inquire About This ${data.vehicle_type}`}>Inquire About This <span className="capitalize">{data.vehicle_type}</span></TrackableButton>
                </section>
              </div>
            </div>

            <InquiryModal
              listing={data}
              isOpen={inquiryModalIsOpen}
              closeModal={() => {
                setInquiryModalIsOpen(false)
              }}
            />
          </Layout>
        )
      }}
    />
  )
}
